import { mirrorColor } from "@corca/tiptap";
import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { ColorPalette, ColorPickerIcon } from "@/components/color-palette/ColorPalette";
import { Icon } from "@/components/icons/Icon";
import { Sidebar } from "@/components/sidebar";
import { SidebarToolbar } from "@/components/sidebar/SidebarToolbar";
import { trackEvent } from "@/utils/trackEvent";
import { twMerge } from "@/utils/twMerge";

import { GraphAlignButtons } from "./GraphAlignButtons";
import { GraphGridType } from "./GraphGridType";

import type { GraphNodeAttrs } from "@/components/tiptap/Graph";
import type { GraphNodeModel } from "@/models/nodes/GraphNodeModel";

type Props = {
  node: GraphNodeModel;
  graphAttrs: GraphNodeAttrs;
};

export const GraphGridStyle = observer(function GraphGridStyle({ node, graphAttrs }: Props) {
  const { doc } = node;
  const { tiptap } = doc.editor;
  const t = useTranslations("sidebar.activeNode");
  const tSidebar = useTranslations("sidebar");

  if (!tiptap) return null;

  const showColorPalette = graphAttrs.gridType !== 5 && graphAttrs.gridType !== 4;

  const onGraphAttributesChange = (attributes: Partial<GraphNodeAttrs>) => {
    tiptap.commands.updateAttributes("graph", attributes);
  };

  return (
    <>
      <div className="flex mb-2.5">
        <GraphGridType doc={doc} value={graphAttrs.gridType} className="min-w-0" />
        {showColorPalette && (
          <ColorPalette
            currentColor={graphAttrs.gridColor}
            tooltipTitle={t("graph.actions.gridColor")}
            onSelect={(gridColor) => {
              onGraphAttributesChange({ gridColor });
              trackEvent("graph_grid_color_changed");
            }}
            className="ml-1.5"
            toggleProps={{
              className: "min-h-[29px]",
              wrapperClassName: "py-0",
            }}
            icon={<ColorPickerIcon color={graphAttrs.gridColor} />}
          />
        )}
      </div>
      <SidebarToolbar label={tSidebar("toolbars.gridStyle")} className="justify-between mb-2">
        <GraphAlignButtons doc={doc} graphAttrs={graphAttrs} />
        <Sidebar.Button
          icon={<Icon name="graph/HideAxes" />}
          aria-label={
            graphAttrs.axesVisible ? t("graph.actions.hideAxes") : t("graph.actions.showAxes")
          }
          title={graphAttrs.axesVisible ? t("graph.actions.hideAxes") : t("graph.actions.showAxes")}
          isActive={!graphAttrs.axesVisible}
          className="w-9.5"
          onClick={() => {
            onGraphAttributesChange({ axesVisible: !graphAttrs.axesVisible });
            trackEvent("graph_axes_visibility_toggled", { visible: !graphAttrs.axesVisible });
          }}
          inverted
        />
        {graphAttrs.axesVisible && (
          <ColorPalette
            currentColor={graphAttrs.axesColor}
            onSelect={(axesColor) => {
              onGraphAttributesChange({ axesColor });
              trackEvent("graph_axes_color_changed");
            }}
            tooltipTitle={t("graph.actions.axesColor")}
            toggleProps={{
              className: "min-h-[31px] w-12",
              wrapperClassName: "py-0",
            }}
            icon={<ColorPickerIcon color={graphAttrs.axesColor} />}
          />
        )}
        <ColorPalette
          currentColor={graphAttrs.bgColor}
          onSelect={(bgColor) => {
            onGraphAttributesChange({ bgColor });
            trackEvent("graph_bg_color_changed");
          }}
          iconClassName="rounded-4"
          tooltipTitle={t("graph.actions.backgroundColor")}
          toggleProps={{
            className: "min-h-[31px] w-12",
            wrapperClassName: "py-0",
          }}
          icon={
            <ColorPickerIcon
              className={twMerge(
                "rounded-3",
                `bg-${graphAttrs.bgColor ?? "white"} dark:bg-${graphAttrs.bgColor ? mirrorColor(graphAttrs.bgColor) : "gray-900"}`,
              )}
            />
          }
        />
      </SidebarToolbar>
    </>
  );
});
